/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { Fragment, memo, useState } from 'react';
import { Box, Flex, List, Spinner, Text } from '@chakra-ui/core';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AppointmentStatus } from '../../../firebase/firestore/documents/appointmentStatus';
import { ReactComponent as _PositionStateDot } from '../../../assets/img/position-state.svg';
import { getTranslatedValue } from '../../../utils/localizedString';
import { AppointmentType, AvailableBookingWithPosition } from '../../../firebase/firestore/documents/appointmentAvaiability';
import { BookingDetailView } from '../bookingDetail/BookingDetailView';
import { BookingDetailContent } from '../bookingDetail/BookingDetailContent';

import { ScheduleIcon } from '../ScheduleIcon';
import colors from '../../../styles/colors';
import { InterviewTimezoneTime } from '../availabilityCanendar/InterviewTimezoneTime';

export type ScheduleByDateProps = {
  listBooking: AvailableBookingWithPosition[];
  isRequestedBookingsLoading?: boolean;
};

export const ScheduleListView = ({ listBooking, isRequestedBookingsLoading }: ScheduleByDateProps): JSX.Element => {
  const [selectedBookingDetail, setSelectedBookingDetail] = useState<AvailableBookingWithPosition>();
  const { i18n, t } = useTranslation();
  const [isBookingDetailOpen, setIsBookingDetailOpen] = useState<boolean>(false);
  const PositionStateDot = memo(_PositionStateDot);

  const loadBookingDetail = (bookingDetail: AvailableBookingWithPosition) => {
    setSelectedBookingDetail(bookingDetail);
    setIsBookingDetailOpen(true);
  };

  const sortedBookingList = listBooking.sort((a, b) => moment(a.booking.date).valueOf() - moment(b.booking.date).valueOf());

  return (
    <Fragment>
      <List p={2} spacing={3} className="schedule-list-wrapper" data-testid="scheduleList" bg="#fff">
        {sortedBookingList.map((scheduleWithCandidateInformation) => {
          const isRequested =
            scheduleWithCandidateInformation.booking.accepted === false ||
            scheduleWithCandidateInformation.booking.shortCode === AppointmentStatus.requested;

          if (isRequested) {
            return (
              <Box key={scheduleWithCandidateInformation.booking.id} data-testid={scheduleWithCandidateInformation.booking.id}>
                <BookingDetailContent bookingDetail={scheduleWithCandidateInformation} isInline />
              </Box>
            );
          }

          return (
            <Box
              className="schedule-box"
              style={{ cursor: 'pointer' }}
              key={scheduleWithCandidateInformation.booking.id}
              data-testid={scheduleWithCandidateInformation.booking.id}
              onClick={() => loadBookingDetail(scheduleWithCandidateInformation)}
              bg="white"
              p={4}
              borderRadius={5}
              boxShadow="sm"
            >
              <Flex align="center">
                <Box className="icon-block">
                  <ScheduleIcon
                    type={scheduleWithCandidateInformation.booking.type as AppointmentType}
                    isAdhoc={scheduleWithCandidateInformation.candidate.appointment?.adhoc}
                    bookingId={scheduleWithCandidateInformation.booking.id || /* istanbul ignore next */ ''}
                  />
                </Box>
                <Box ml="3">
                  <Flex align="center" mb={1}>
                    <Text fontWeight="bold" fontSize="sm" lineHeight={1} mr={2}>
                      {getTranslatedValue(scheduleWithCandidateInformation.candidate.jobTitle, i18n.language)}
                    </Text>
                  </Flex>
                  <Text
                    className="candidate-name"
                    color="orange.500"
                    fontSize="0.65rem"
                    fontWeight="normal"
                    mb={1}
                    lineHeight={1}
                  >
                    {`${scheduleWithCandidateInformation.candidate.firstName} ${scheduleWithCandidateInformation.candidate.lastName}`}
                  </Text>
                  <Text className="candidate-name" fontSize="0.65rem" fontWeight="normal" mb={1} lineHeight={1}>
                    {scheduleWithCandidateInformation.candidate.businessName}
                  </Text>
                  <InterviewTimezoneTime
                    startTime={scheduleWithCandidateInformation.booking.date}
                    endTime={moment(scheduleWithCandidateInformation.booking.date)
                      .add(scheduleWithCandidateInformation.booking.duration, 'minutes')
                      .toDate()}
                    isShort
                    size="compact"
                  />
                  {scheduleWithCandidateInformation.candidate.dismissed && (
                    <Flex alignItems="center">
                      <PositionStateDot color={colors.red[600]} />

                      <Text
                        fontSize="xs"
                        color={colors.red[600]}
                        lineHeight="shorter"
                        pt={1}
                        maxW={{ base: '11rem', md: '12rem', lg: '18rem' }}
                      >
                        {t('candidateStatus:DISMISSED')}
                      </Text>
                    </Flex>
                  )}
                </Box>
              </Flex>
            </Box>
          );
        })}
        {isRequestedBookingsLoading && (
          <Flex justify="center" p={4}>
            <Spinner />
          </Flex>
        )}
      </List>
      {selectedBookingDetail && (
        <BookingDetailView
          isOpen={isBookingDetailOpen}
          onClose={
            // istanbul ignore next
            () => setIsBookingDetailOpen(false)
          }
          bookingDetail={selectedBookingDetail}
        />
      )}
    </Fragment>
  );
};
